import { useState, useEffect } from 'react';

interface MobileDetection {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isWeChat: boolean;
  isPWA: boolean;
  screenSize: {
    width: number;
    height: number;
  };
  orientation: 'portrait' | 'landscape';
  safeArea: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

export const useMobile = (): MobileDetection => {
  const [mobileInfo, setMobileInfo] = useState<MobileDetection>({
    isMobile: false,
    isIOS: false,
    isAndroid: false,
    isWeChat: false,
    isPWA: false,
    screenSize: { width: 0, height: 0 },
    orientation: 'portrait',
    safeArea: { top: 0, bottom: 0, left: 0, right: 0 }
  });

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // 设备检测
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);
    const isWeChat = /micromessenger/i.test(userAgent);
    
    // PWA 检测
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window as any).navigator.standalone === true;

    // 屏幕尺寸和方向
    const updateScreenInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const orientation = width > height ? 'landscape' : 'portrait';
      
      // 安全区域获取（主要针对iOS刘海屏）
      const safeArea = {
        top: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0', 10),
        bottom: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0', 10),
        left: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sal') || '0', 10),
        right: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sar') || '0', 10)
      };

      setMobileInfo(prev => ({
        ...prev,
        screenSize: { width, height },
        orientation,
        safeArea
      }));
    };

    // 初始化设备信息
    setMobileInfo({
      isMobile,
      isIOS,
      isAndroid,
      isWeChat,
      isPWA,
      screenSize: { width: window.innerWidth, height: window.innerHeight },
      orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
      safeArea: { top: 0, bottom: 0, left: 0, right: 0 }
    });

    // 监听屏幕变化
    window.addEventListener('resize', updateScreenInfo);
    window.addEventListener('orientationchange', updateScreenInfo);

    // 监听PWA安装事件
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // 可以在这里存储事件，稍后显示安装提示
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('resize', updateScreenInfo);
      window.removeEventListener('orientationchange', updateScreenInfo);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  return mobileInfo;
};

// 触觉反馈Hook
export const useHapticFeedback = () => {
  const vibrate = (pattern: number | number[] = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const lightImpact = () => vibrate(10);
  const mediumImpact = () => vibrate(50);
  const heavyImpact = () => vibrate([100, 50, 100]);

  return {
    vibrate,
    lightImpact,
    mediumImpact,
    heavyImpact
  };
};

// 网络状态Hook
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState<string>('unknown');

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 获取网络连接类型（如果支持）
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      setConnectionType(connection.effectiveType || 'unknown');
      
      const handleConnectionChange = () => {
        setConnectionType(connection.effectiveType || 'unknown');
      };
      
      connection.addEventListener('change', handleConnectionChange);
      
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        connection.removeEventListener('change', handleConnectionChange);
      };
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, connectionType };
};

// 电池状态Hook
export const useBatteryStatus = () => {
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [isCharging, setIsCharging] = useState<boolean | null>(null);

  useEffect(() => {
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        setBatteryLevel(battery.level);
        setIsCharging(battery.charging);

        const handleLevelChange = () => setBatteryLevel(battery.level);
        const handleChargingChange = () => setIsCharging(battery.charging);

        battery.addEventListener('levelchange', handleLevelChange);
        battery.addEventListener('chargingchange', handleChargingChange);

        return () => {
          battery.removeEventListener('levelchange', handleLevelChange);
          battery.removeEventListener('chargingchange', handleChargingChange);
        };
      });
    }
  }, []);

  return { batteryLevel, isCharging };
};